import { DatabaseFieldType, type DatabaseValue } from '@databases/types/database';
import { type ValueChange } from '@databases/utils/database/changesets';
import Delta from 'quill-delta';

export type ChangeListElement = { val: DatabaseValue; change: ValueChange | null };

/**
 * A globally accessible state for managing the current cell editing session.
 *
 * The `cellEditingSession` object maintains the state of a currently active
 * cell editing session, which tracks the current field being edited by ID
 * and a list of current changes that are stored in a format to be sent to
 * addChangeToDraft.
 *
 * Structure:
 * - `currentFieldId`: The identifier of the field currently being edited.
 *   - Type: `string | null`
 *   - Initially `null`, indicating no active editing session.
 * - `changes`: An array of changes made during the current editing session.
 *   - Type: `ChangeListElement[]`
 *   - Initially an empty array.
 *
 * Usage:
 * - Start a session by calling `startCellEditingSessionForField(fieldId)`.
 * - Add changes using `addChangeToEditSession(change)`.
 * - Retrieve pending changes for a specific field with `getPendingChangesForField(fieldId, fieldType)`.
 *   - This function returns an array of changes in the format expected by `addChangeToDraft`.
 * - Clear all session data with `clearCellEditingSessionChanges()`.
 *
 * This is currently used in the `FieldValueEdit` component to manage the state
 * of the current editing session and track changes made to a field while a sort
 * is being applied to it to be dispatched on blur when the user finishes editing.
 */
export const cellEditingSession: {
  currentFieldId: string | null;
  changes: ChangeListElement[];
} = {
  currentFieldId: null,
  changes: [],
};

export function startCellEditingSessionForField(fieldId: string) {
  cellEditingSession.currentFieldId = fieldId;
  cellEditingSession.changes = [];
}

export function addChangeToEditSession(change: ChangeListElement) {
  cellEditingSession.changes.push(change);
}

export function clearCellEditingSessionChanges() {
  cellEditingSession.currentFieldId = null;
  cellEditingSession.changes = [];
}

/**
 * Returns a list of sanitized changes for a specific field.
 * Ex: Merge multiple text change deltas into a single delta.
 *
 * @param fieldId - The identifier of the field to retrieve changes for.
 * @param fieldType - The type of the field being edited.
 */
export function getPendingChangesForField(fieldId: string, fieldType: DatabaseFieldType) {
  if (
    cellEditingSession.currentFieldId !== fieldId ||
    cellEditingSession.changes.length === 0
  ) {
    return [];
  }

  let sanitizedChanges: ChangeListElement[] = [];

  if (fieldType === DatabaseFieldType.TEXT) {
    const mergedDelta = mergeValueChangeTextDeltas(
      cellEditingSession.changes.map((change) => change.change)
    );

    sanitizedChanges.push({
      val: cellEditingSession.changes[cellEditingSession.changes.length - 1].val,
      change: {
        delta: mergedDelta.ops,
        type: DatabaseFieldType.TEXT,
      },
    });
  } else {
    sanitizedChanges = cellEditingSession.changes;
  }

  return sanitizedChanges;
}

function mergeValueChangeTextDeltas(deltas: (ValueChange | null)[]) {
  return deltas.reduce((accumulatedDelta, currentChange) => {
    if (
      currentChange &&
      currentChange.type === DatabaseFieldType.TEXT &&
      'delta' in currentChange
    ) {
      return accumulatedDelta.compose(new Delta(currentChange.delta));
    }

    return accumulatedDelta;
  }, new Delta());
}
